import { BindingError } from "../BindingError.js";
import { PRIMARY_KEY_NAME } from "../bindingTypes.js";
import { EntitySubTreeMarker } from "../markers/EntitySubTreeMarker.js";
import { EntityListSubTreeMarker } from "../markers/EntityListSubTreeMarker.js";
import { assertNever } from "../utils/assertNever.js";
import { FieldMarker } from "../markers/FieldMarker.js";
import { HasOneRelationMarker } from "../markers/HasOneRelationMarker.js";
import { HasManyRelationMarker } from "../markers/HasManyRelationMarker.js";
import { ServerId } from "../accessorTree/RuntimeId.js";
class RequestResponseNormalizer {
  static mergeInQueryResponse(original, newPersistedData, markerTree) {
    const { subTreeDataStore, persistedEntityDataStore } = original;
    for (const [placeholder, marker] of markerTree.subTrees) {
      const data = newPersistedData[placeholder] ?? null;
      if (marker instanceof EntitySubTreeMarker) {
        if (data === null) {
          subTreeDataStore.delete(placeholder);
        } else {
          if (Array.isArray(data)) {
            throw new BindingError();
          }
          const id = this.processEntityData(persistedEntityDataStore, data, marker.fields.markers);
          subTreeDataStore.set(placeholder, id);
        }
      } else if (EntityListSubTreeMarker) {
        const subTreeListIds = /* @__PURE__ */ new Set();
        if (!Array.isArray(data)) {
          throw new BindingError();
        }
        for (const row of data) {
          const id = this.processEntityData(persistedEntityDataStore, row, marker.fields.markers);
          subTreeListIds.add(id.value);
        }
        subTreeDataStore.set(placeholder, subTreeListIds);
      } else {
        throw new BindingError();
      }
    }
  }
  static mergeInMutationResponse(original, newPersistedData, operations) {
    const { subTreeDataStore, persistedEntityDataStore } = original;
    for (const operation of operations) {
      const { subTreePlaceholder, subTreeType, alias } = operation;
      const receivedData = newPersistedData[alias];
      if (!receivedData || Array.isArray(receivedData)) {
        throw new BindingError();
      }
      if (operation.type === "update" || operation.type === "create") {
        const id = this.processEntityData(persistedEntityDataStore, receivedData, operation.markers);
        if (subTreeType === "single") {
          subTreeDataStore.set(subTreePlaceholder, id);
        } else if (subTreeType === "list") {
          const list = subTreeDataStore.get(subTreePlaceholder);
          if (list instanceof Set) {
            list.add(id.value);
          } else if (list === void 0) {
            subTreeDataStore.set(subTreePlaceholder, /* @__PURE__ */ new Set([id.value]));
          } else {
            throw new BindingError();
          }
        } else {
          return assertNever();
        }
      } else if (operation.type === "delete") {
        const id = this.extractId(receivedData);
        persistedEntityDataStore.delete(id.uniqueValue);
        if (subTreeType === "single") {
          subTreeDataStore.delete(subTreePlaceholder);
        } else if (subTreeType === "list") {
          const list = subTreeDataStore.get(subTreePlaceholder);
          if (!(list instanceof Set)) {
            throw new BindingError();
          }
          list.delete(id.value);
        } else {
          return assertNever();
        }
      } else {
        return assertNever();
      }
    }
  }
  static processEntityData(entityMap, entityData, fields) {
    const id = this.extractId(entityData);
    const presentEntityData = entityMap.get(id.uniqueValue) ?? /* @__PURE__ */ new Map();
    entityMap.set(id.uniqueValue, presentEntityData);
    for (const [placeholder, field] of fields) {
      const value = entityData[placeholder];
      if (field instanceof FieldMarker) {
        presentEntityData.set(placeholder, value);
      } else if (field instanceof HasOneRelationMarker) {
        if (value === null) {
          presentEntityData.set(placeholder, null);
        } else {
          presentEntityData.set(placeholder, this.processEntityData(entityMap, value, field.fields.markers));
        }
      } else if (field instanceof HasManyRelationMarker) {
        const ids = presentEntityData.get(placeholder) ?? /* @__PURE__ */ new Set();
        presentEntityData.set(placeholder, ids);
        if (!(ids instanceof Set)) {
          throw new BindingError();
        }
        ids.clear();
        for (const row of value) {
          const id2 = this.processEntityData(entityMap, row, field.fields.markers);
          ids.add(id2.value);
        }
      } else {
        throw new Error();
      }
    }
    return id;
  }
  static extractId(entityData) {
    const primaryKey = entityData[PRIMARY_KEY_NAME];
    if (primaryKey === void 0) {
      throw new BindingError(`The server has responded with an entity that lacks a primary key.`);
    }
    return new ServerId(primaryKey, entityData.__typename);
  }
}
export {
  RequestResponseNormalizer
};
//# sourceMappingURL=RequestResponseNormalizer.js.map
